import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { Icon } from "@/components/site/Icon";

/**
 * Generic cards grid. Handles multiple variants automatically:
 *  - "service" style : title + description + Learn more (when card.href starts with /services/)
 *  - "industry-mini" style: compact chip with icon + description as label (industry cards from home)
 *  - "pillar" style : plain title + description card with gold top-bar
 *  - "content" style : title + description (values on About, "what we do" etc.)
 *
 * Layout is determined by heading text + card shape.
 */
function isServiceCard(c) {
  return c && typeof c.href === "string" && c.href.startsWith("/services/");
}
function isIndustryMiniCard(c) {
  return c && typeof c.href === "string" && c.href.startsWith("/industries/") && !c.description?.match(/[.,]/);
}

function ServiceCard({ card, index }) {
  return (
    <Link
      to={card.href}
      data-testid={card.testid || `service-card-${index}`}
      className="group relative flex flex-col bg-white border border-mist-soft rounded-md p-7 lg:p-8 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:shadow-[0_10px_30px_-15px_rgba(16,29,54,0.15)] hover:border-mist-navy/15"
    >
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[3px] bg-mist-gold rounded-t-md scale-x-0 origin-left transition-transform duration-500 group-hover:scale-x-100"
      />
      <div className="h-11 w-11 rounded-full bg-mist-navy/[0.06] grid place-items-center mb-6 transition-colors group-hover:bg-mist-gold/15">
        <Icon name={card.icon} className="h-6 w-6 text-mist-navy" />
      </div>
      <h3 className="font-sans font-semibold text-xl leading-snug text-mist-navy min-h-[3.4rem] tracking-tight">{card.title}</h3>
      {card.description && (
        <p className="mt-3 text-sm text-mist-ink/70 leading-relaxed line-clamp-4">{card.description}</p>
      )}
      <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-mist-navy group-hover:text-mist-gold transition-colors">
        Learn more
        <ArrowUpRight className="h-3.5 w-3.5" aria-hidden />
      </span>
    </Link>
  );
}

function IndustryMiniCard({ card, index }) {
  const label = card.title || card.description || "";
  return (
    <Link
      to={card.href}
      data-testid={card.testid || `industry-card-${index}`}
      className="group flex items-center gap-4 bg-white border border-mist-soft rounded-md p-5 transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-mist-navy/15 hover:shadow-[0_10px_30px_-15px_rgba(16,29,54,0.15)]"
    >
      <div className="h-10 w-10 rounded-full bg-mist-navy/[0.06] grid place-items-center transition-colors group-hover:bg-mist-gold/20 shrink-0">
        <Icon name={card.icon} className="h-5 w-5 text-mist-navy" />
      </div>
      <div className="min-w-0">
        <div className="font-medium text-sm text-mist-navy truncate">{label}</div>
      </div>
    </Link>
  );
}

function PillarCard({ card, index, theme }) {
  const textColor = theme === "navy" ? "text-white" : "text-mist-navy";
  const descColor = theme === "navy" ? "text-white/70" : "text-mist-ink/70";
  const bgClass = theme === "navy" ? "bg-white/[0.03] border-white/10" : "bg-mist-off border-mist-soft";
  return (
    <div
      data-testid={card.testid || `pillar-${index}`}
      className={`relative border rounded-md p-8 ${bgClass}`}
    >
      <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-mist-gold rounded-t-md" />
      {card.icon && <Icon name={card.icon} className={`h-6 w-6 mb-5 ${textColor === "text-white" ? "text-mist-gold" : "text-mist-navy"}`} />}
      {(card.title || card.description) && (
        <h3 className={`font-sans font-semibold text-xl leading-snug tracking-tight ${textColor}`}>{card.title || card.description}</h3>
      )}
      {card.title && card.description && card.title !== card.description && (
        <p className={`mt-3 text-sm ${descColor} leading-relaxed`}>{card.description}</p>
      )}
    </div>
  );
}

function ContentCard({ card, index, theme }) {
  const titleColor = theme === "navy" ? "text-white" : "text-mist-navy";
  const descColor = theme === "navy" ? "text-white/70" : "text-mist-ink/70";
  const bgClass = theme === "navy" ? "bg-white/[0.03] border-white/10" : "bg-white border-mist-soft";
  return (
    <div
      data-testid={card.testid || `content-card-${index}`}
      className={`border rounded-md p-6 ${bgClass}`}
    >
      {card.title && <div className={`font-sans font-semibold text-lg tracking-tight ${titleColor}`}>{card.title}</div>}
      {card.description && card.description !== card.title && (
        <p className={`mt-2 text-sm ${descColor} leading-relaxed`}>{card.description}</p>
      )}
    </div>
  );
}

export default function CardsGrid({ section }) {
  const {
    eyebrow,
    heading,
    paragraphs = [],
    cards = [],
    theme = "white",
  } = section;

  const bgClass = theme === "navy" ? "bg-mist-navy text-white" : theme === "warm" ? "bg-mist-warm" : theme === "off" ? "bg-mist-off" : "bg-white";
  const headingColor = theme === "navy" ? "text-white" : "text-mist-navy";
  const paraColor = theme === "navy" ? "text-white/70" : "text-mist-ink/75";

  // Detect layout variant by card shape
  const allServices = cards.length > 0 && cards.every(isServiceCard);
  const allIndustryMini = cards.length > 0 && cards.every(isIndustryMiniCard);
  const hasIcons = cards.some((c) => c.icon);

  // Two-column layout when only 2 grid children and one contains the heading (rich-text split)
  const isTwoColumnSplit =
    cards.length === 2 &&
    !allServices &&
    !allIndustryMini &&
    cards.some((c) => (c.title || "").length > 40);

  return (
    <section className={`${bgClass} py-24 lg:py-32`}>
      <div className="container-mist">
        <div className="max-w-2xl text-left">
          {heading && (
            <h2
              className={`font-display text-3xl sm:text-4xl lg:text-[2.75rem] leading-[1.1] tracking-tight ${headingColor}`}
            >
              {heading}
            </h2>
          )}
          {(eyebrow || heading) && <div className="mt-4 gold-divider" />}
          {paragraphs[0] && (
            <p className={`mt-5 text-[15px] leading-relaxed ${paraColor}`}>{paragraphs[0]}</p>
          )}
        </div>

        {isTwoColumnSplit ? (
          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
            {cards.map((c, i) => {
              const titleIsHeading = c.title && heading && c.title.trim() === heading.trim();
              const paras = c.paragraphs || (c.description ? [c.description] : []);
              return (
                <div key={i} className="prose max-w-none">
                  {c.title && !titleIsHeading && (
                    <div className={`font-sans font-semibold text-2xl leading-snug tracking-tight ${headingColor}`}>{c.title}</div>
                  )}
                  <div className={`${titleIsHeading ? "" : "mt-3"} space-y-3`}>
                    {paras.map((p, k) => (
                      <p key={k} className={`text-[15px] ${paraColor} leading-relaxed`}>
                        {p}
                      </p>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : allServices ? (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((c, i) => (
              <ServiceCard key={c.testid || i} card={c} index={i} />
            ))}
          </div>
        ) : allIndustryMini ? (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {cards.map((c, i) => (
              <IndustryMiniCard key={c.testid || i} card={c} index={i} />
            ))}
          </div>
        ) : hasIcons && cards.length <= 4 ? (
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {cards.map((c, i) => (
              <PillarCard key={i} card={c} index={i} theme={theme} />
            ))}
          </div>
        ) : (
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {cards.map((c, i) => (
              <ContentCard key={i} card={c} index={i} theme={theme} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
